import {
	Box,
	ChevronRight,
	ChevronsUpDown,
	Contact,
	File,
	HelpCircle,
	Factory,
	LayoutGrid,
	LogOut,
	Menu,
	Moon,
	Settings,
	Sun,
	Shield,
	Truck,
	UserCog,
	Users,
	type LucideIcon,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { FeatureErrorBoundary } from '../components/FeatureErrorBoundary';
import { PageMetaProvider, usePageMeta } from '../components/layout/PageMeta';
import { RouteProgress } from '../components/RouteProgress';
import { InitialsAvatar } from '../components/ui/initials-avatar';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useThemeStore } from '../stores/theme';
import { Logo } from '../components/ui/logo';
import { CurrencySelector } from '../components/CurrencySelector';
import { NotificationBell } from '../components/NotificationBell';
import { Separator } from '../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import { AdminUserType, useAuth } from '../hooks/useAuth';

/* ── Navigation structure ── */

const NAV_GROUPS = [
	{
		label: 'Workspace',
		items: [{ id: 'dashboard', label: 'Dashboard', icon: LayoutGrid, path: '/dashboard' }],
	},
	{
		label: 'Creation',
		items: [{ id: 'products', label: 'Products', icon: Box, path: '/products' }],
	},
	{
		label: 'Supply Chain',
		items: [
			{ id: 'quotes', label: 'Quotes', icon: File, path: '/quotes' },
			{ id: 'orders', label: 'Orders', icon: Truck, path: '/orders' },
			{ id: 'suppliers', label: 'Manufacturers', icon: Factory, path: '/suppliers' },
			{ id: 'vetters', label: 'Vetters', icon: Shield, path: '/vetters' },
		],
	},
	{
		label: 'Organization',
		items: [
			{ id: 'clients', label: 'Clients', icon: Users, path: '/clients' },
			{ id: 'contacts', label: 'Contacts', icon: Contact, path: '/contacts' },
			{ id: 'users', label: 'Users', icon: UserCog, path: '/users' },
			{ id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
		],
	},
];

/* ── Nav Item ── */

function NavItem({
	icon: Icon,
	label,
	active,
	onClick,
}: {
	icon: LucideIcon;
	label: string;
	active: boolean;
	onClick: () => void;
}) {
	return (
		<button
			onClick={onClick}
			className={cn(
				'relative mb-px flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left font-sans text-sm font-[450] transition-[background,color] duration-[120ms] ease-out',
				active
					? 'bg-nav-active text-text-1'
					: 'text-text-2 hover:bg-nav-hover hover:text-text-1'
			)}
		>
			{active && (
				<span className="absolute -left-2 bottom-1/4 top-1/4 w-0.5 rounded-sm bg-accent" />
			)}
			<Icon size={15} className={cn('shrink-0', active ? 'text-text-1' : 'text-text-3')} />
			<span className="flex-1">{label}</span>
		</button>
	);
}

/* ── Sidebar ── */

function Sidebar({
	activeId,
	onNav,
	user,
	onLogout,
}: {
	activeId: string;
	onNav: (id: string) => void;
	user: AdminUserType | null;
	onLogout: () => void;
}) {
	return (
		<aside className="flex h-full flex-col overflow-hidden border-r border-line-1 bg-sidebar">
			{/* Brand */}
			<div className="flex items-center gap-2.5 px-4 pt-4 pb-4 text-text-1">
				<Logo />
				<div className="flex flex-col leading-tight">
					<span className="text-base font-medium -tracking-[0.01em]">05 Creation</span>
					<span className="text-xs tracking-[0.04em] text-text-3">Studio</span>
				</div>
			</div>

			{/* Nav groups */}
			<nav className="scroll-thin flex-1 overflow-y-auto px-2">
				{NAV_GROUPS.map((group) => (
					<div key={group.label} className="mb-3.5">
						<div className="px-2.5 pt-1.5 pb-1 text-2xs font-medium uppercase tracking-[0.12em] text-text-4">
							{group.label}
						</div>
						{group.items.map((item) => (
							<NavItem
								key={item.id}
								icon={item.icon}
								label={item.label}
								active={activeId === item.id}
								onClick={() => onNav(item.id)}
							/>
						))}
					</div>
				))}
			</nav>

			{/* Bottom: user */}
			<div className="border-t border-line-1 px-2 pt-2.5 pb-3">
				<DropdownMenu>
					<DropdownMenuTrigger className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-nav-hover focus-visible:outline-none data-[popup-open]:bg-nav-hover">
						<InitialsAvatar name={user?.email || 'Admin'} size={26} />
						<div className="min-w-0 flex-1">
							<div className="truncate text-xs font-medium">
								{user?.email || 'Admin'}
							</div>
							<div className="text-xs capitalize text-text-3">
								{user?.role?.toLowerCase() || 'User'}
							</div>
						</div>
						<ChevronsUpDown size={14} className="shrink-0 text-text-4" />
					</DropdownMenuTrigger>
					<DropdownMenuContent side="top" align="start" className="w-[188px]">
						<DropdownMenuItem variant="destructive" onClick={onLogout}>
							<LogOut size={14} /> Log out
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</aside>
	);
}

/* ── Theme toggle ── */

function ThemeToggle() {
	const theme = useThemeStore((s) => s.theme);
	const toggleTheme = useThemeStore((s) => s.toggleTheme);
	const nextIsLight = theme === 'dark';
	const label = nextIsLight ? 'Switch to light theme' : 'Switch to dark theme';

	return (
		<Tooltip>
			<TooltipTrigger
				onClick={toggleTheme}
				aria-label={label}
				className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground"
			>
				{nextIsLight ? <Sun size={16} /> : <Moon size={16} />}
			</TooltipTrigger>
			<TooltipContent>{label}</TooltipContent>
		</Tooltip>
	);
}

/* ── Topbar ── */

function Topbar({ onMenuToggle }: { onMenuToggle?: () => void }) {
	const { title, breadcrumbs } = usePageMeta();

	return (
		<div className="flex h-14 shrink-0 items-center gap-4 border-b border-line-1 px-4 md:px-6">
			<button
				onClick={onMenuToggle}
				className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground md:hidden"
				aria-label="Menu"
			>
				<Menu size={18} />
			</button>
			<div className="min-w-0">
				<div className="truncate text-base font-medium -tracking-[0.01em] text-text-1">
					{title}
				</div>
				{breadcrumbs.length > 0 && (
					<nav
						aria-label="Breadcrumb"
						className="mt-px flex items-center gap-1 text-xs max-sm:hidden"
					>
						<Link to="/" className="text-text-4 transition-colors hover:text-text-1">
							Home
						</Link>
						{breadcrumbs.map((crumb, i) => {
							const isLast = i === breadcrumbs.length - 1;
							return (
								<span key={i} className="flex items-center gap-1">
									<ChevronRight size={10} className="text-text-4 opacity-60" />
									{isLast || !crumb.path ? (
										<span className="truncate text-text-4">{crumb.label}</span>
									) : (
										<Link
											to={crumb.path}
											className="text-text-4 transition-colors hover:text-text-1"
										>
											{crumb.label}
										</Link>
									)}
								</span>
							);
						})}
					</nav>
				)}
			</div>
			<div className="flex-1" />
			<CurrencySelector />
			<Separator orientation="vertical" className="h-4 bg-line-1" />
			<TooltipProvider delay={300}>
				<ThemeToggle />
				<Tooltip>
					<TooltipTrigger className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground">
						<HelpCircle size={16} />
					</TooltipTrigger>
					<TooltipContent>Help</TooltipContent>
				</Tooltip>
			</TooltipProvider>
			<NotificationBell />
		</div>
	);
}

/* ── Layout ── */

export function PlatformLayout() {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout } = useAuth();
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);
	useScrollRestoration(scrollRef);

	const activeId =
		NAV_GROUPS.flatMap((g) => g.items).find((item) => location.pathname.startsWith(item.path))
			?.id || 'dashboard';

	const handleNav = (id: string) => {
		setSidebarOpen(false);
		const item = NAV_GROUPS.flatMap((g) => g.items).find((i) => i.id === id);
		if (item) navigate(item.path);
	};

	return (
		<PageMetaProvider>
			<RouteProgress />
			<div className="relative h-full w-full overflow-hidden bg-canvas font-sans text-text-1 md:grid md:grid-cols-[var(--sidebar-width)_1fr]">
				{/* Mobile overlay */}
				{sidebarOpen && (
					<div
						className="fixed inset-0 z-40 bg-black/60 md:hidden"
						onClick={() => setSidebarOpen(false)}
					/>
				)}

				{/* Sidebar — fixed drawer on mobile, static on md+ */}
				<div
					className={cn(
						'fixed inset-y-0 left-0 z-50 w-[var(--sidebar-width)] transition-transform duration-200 ease-out md:static md:translate-x-0',
						sidebarOpen ? 'translate-x-0' : '-translate-x-full'
					)}
				>
					<Sidebar activeId={activeId} onNav={handleNav} user={user} onLogout={logout} />
				</div>

				<main className="flex h-full flex-col overflow-hidden bg-canvas">
					<Topbar onMenuToggle={() => setSidebarOpen(true)} />
					<div ref={scrollRef} className="scroll-thin flex-1 overflow-auto">
						{/* No width cap here — each page owns its own content column
						    (max-w-[var(--content-max)]). A second cap at this level
						    silently overrode it and had to be kept in sync. */}
						<div className="flex w-full flex-1 flex-col">
							<FeatureErrorBoundary key={location.pathname}>
								<Outlet />
							</FeatureErrorBoundary>
						</div>
					</div>
				</main>
			</div>
		</PageMetaProvider>
	);
}
